import {
  TttCalculationResult,
  TttIsolineData,
  TttRegionHint,
  TttTransformationLevel
} from '../types/Calculation';

export const TTT_ISOLINE_LEVELS: TttTransformationLevel[] = [25, 50, 75];

export const TTT_REGION_HINTS: TttRegionHint[] = [
  'intercritical_ferrite',
  'eutectoid_pearlite',
  'bainite'
];

export const TTT_TIME_AXIS_RANGE: [number, number] = [-1, 4];

const levelColors: Record<TttTransformationLevel, string> = {
  25: '#1f77b4',
  50: '#ff7f0e',
  75: '#2ca02c'
};

const MAX_CONTIGUOUS_LOG_TIME_STEP = 1;

export interface TttPlotTrace {
  x: Array<number | null>;
  y: Array<number | null>;
  type: 'scatter';
  mode: 'lines';
  name: string;
  legendgroup?: string;
  showlegend: boolean;
  hoverinfo?: 'skip';
  hovertemplate?: string;
  connectgaps?: boolean;
  line: {
    color: string;
    dash: 'solid' | 'dash' | 'dot';
    width: number;
  };
}

export const hasTttIsolineData = (
  result: TttCalculationResult
): result is TttCalculationResult & TttIsolineData =>
  Array.isArray(result.level_pct) &&
  Array.isArray(result.log10_plot_time) &&
  Array.isArray(result.region_hint) &&
  Array.isArray(result.temperature_C);

export const buildTttIsolineTraces = (
  result: TttCalculationResult
): TttPlotTrace[] => {
  if (!hasTttIsolineData(result)) {
    return [];
  }

  const traces: TttPlotTrace[] = [];

  TTT_ISOLINE_LEVELS.forEach(level => {
    TTT_REGION_HINTS.forEach(region => {
      const x: Array<number | null> = [];
      const y: Array<number | null> = [];

      let index = 0;
      while (index < result.level_pct.length) {
        const matches =
          result.level_pct[index] === level &&
          result.region_hint[index] === region;

        if (!matches) {
          index += 1;
          continue;
        }

        const runStart = index;
        while (
          index + 1 < result.level_pct.length &&
          result.level_pct[index + 1] === level &&
          result.region_hint[index + 1] === region
        ) {
          index += 1;
        }
        const runEnd = index;
        const runLength = runEnd - runStart + 1;
        const previousIsAnotherRegion =
          runStart > 0 &&
          result.level_pct[runStart - 1] === level &&
          result.region_hint[runStart - 1] !== region;
        const nextIsAnotherRegion =
          runEnd + 1 < result.level_pct.length &&
          result.level_pct[runEnd + 1] === level &&
          result.region_hint[runEnd + 1] !== region;
        const plottedStart =
          runLength > 2 && previousIsAnotherRegion ? runStart + 1 : runStart;
        const plottedEnd =
          runLength > 2 && nextIsAnotherRegion ? runEnd - 1 : runEnd;

        if (x.length > 0) {
          x.push(null);
          y.push(null);
        }

        let previousTemperatureDirection = 0;
        for (let pointIndex = plottedStart; pointIndex <= plottedEnd; pointIndex += 1) {
          if (pointIndex > plottedStart) {
            const logTimeStep = Math.abs(
              result.log10_plot_time[pointIndex] -
              result.log10_plot_time[pointIndex - 1]
            );
            const temperatureStep =
              result.temperature_C[pointIndex] -
              result.temperature_C[pointIndex - 1];
            const temperatureDirection = temperatureStep === 0
              ? 0
              : temperatureStep > 0 ? 1 : -1;
            const reversesTemperatureDirection =
              previousTemperatureDirection !== 0 &&
              temperatureDirection !== 0 &&
              temperatureDirection !== previousTemperatureDirection;

            if (
              logTimeStep > MAX_CONTIGUOUS_LOG_TIME_STEP ||
              reversesTemperatureDirection
            ) {
              x.push(null);
              y.push(null);
            }

            if (temperatureDirection !== 0) {
              previousTemperatureDirection = temperatureDirection;
            }
          }

          x.push(Math.pow(10, result.log10_plot_time[pointIndex]));
          y.push(result.temperature_C[pointIndex]);
        }

        index += 1;
      }

      traces.push({
        x,
        y,
        type: 'scatter',
        mode: 'lines',
        name: `${level}% transformed`,
        legendgroup: `ttt-isoline-${level}`,
        showlegend: false,
        connectgaps: false,
        hovertemplate:
          `${level}% transformed<br>` +
          'Time: %{x:.4g} s<br>' +
          'Temperature: %{y:.2f} °C<extra></extra>',
        line: {
          color: levelColors[level],
          dash: 'dot',
          width: 2
        }
      });
    });
  });

  return traces;
};

export const buildTttIsolineLegendTraces = (): TttPlotTrace[] =>
  TTT_ISOLINE_LEVELS.map(level => ({
    x: [null],
    y: [null],
    type: 'scatter',
    mode: 'lines',
    name: `${level}% transformed`,
    legendgroup: `ttt-isoline-${level}`,
    showlegend: true,
    hoverinfo: 'skip',
    line: {
      color: levelColors[level],
      dash: 'dot',
      width: 2
    }
  }));

export const buildTttIsolineCsvRows = (
  result: TttCalculationResult
): string[][] => {
  if (!hasTttIsolineData(result)) {
    return [];
  }

  const rows: string[][] = [
    [],
    ['TTT isolines'],
    [
      'Level (%)',
      'Region',
      'Temperature (°C)',
      'log10 Time (s)',
      'Time (s)'
    ]
  ];

  result.level_pct.forEach((level, index) => {
    const logTime = result.log10_plot_time[index];
    rows.push([
      String(level),
      result.region_hint[index],
      String(result.temperature_C[index]),
      String(logTime),
      String(Math.pow(10, logTime))
    ]);
  });

  return rows;
};
